// src/screens/admin/VuelosEstadisticas.jsx
// Sub-sección "Estadísticas" del panel Vuelos Guyana: cuántas visitas tiene
// el enlace público y el flujo autenticado, cuántos visitantes únicos
// aproximados, y qué ciudades/destinos/fechas se revisan más. Todo agregado
// en el cliente a partir de vuelos_eventos (mismo criterio que el resto del
// panel admin, ej. VuelosRutasPublicadas agrupa localmente).
//
// Dos sub-pestañas: "Resumen" (la foto fija de siempre para el período
// elegido) y "Dashboard" (día por día, con comparación contra el mismo día
// de la semana anterior y nuevos vs. que regresan) -- Yoedis pidió poder ver
// tendencia en el tiempo, no solo un total acumulado.
import React, { useState, useEffect } from 'react';
import {
  getVuelosEventos, getGestores, DESTINO_CATEGORIAS, isVuelosTrackingOptedOut, setVuelosTrackingOptOut,
} from '../../services/vuelosGuyanaService';

// dias: null = sin corte, historial completo (sin límite).
const PERIODOS = [
  { dias: 7, label: '7 días' },
  { dias: 30, label: '30 días' },
  { dias: 90, label: '90 días' },
  { dias: null, label: 'Todo' },
];

// 'j' = canal de Luis Omar (el único hoy). Si en el futuro hay más canales,
// agregar aquí su código y nombre. Los gestores NO se hardcodean acá -- se
// arman solos abajo a partir de vuelos_gestores, para que un gestor nuevo
// aparezca en el filtro sin tocar código.
const CANALES = { j: 'Luis Omar' };

const labelDestino = (id) => DESTINO_CATEGORIAS.find((c) => c.id === id)?.label || id;

const formatFechaCorta = (fechaStr) =>
  new Date(fechaStr + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short' });

function topN(items, n = 8) {
  return [...items].sort((a, b) => b.count - a.count).slice(0, n);
}

// Huso horario fijo para agregar horarios/días de semana -- Yoedis es el
// único que mira este panel, así que fijarlo en vez de usar el del
// navegador de quien lo abra evita que el mismo dato cuente distinto según
// desde dónde se consulte. created_at siempre se guarda en UTC.
const TIMEZONE = 'America/Sao_Paulo';

const getHoraLocal = (createdAt) => parseInt(
  new Intl.DateTimeFormat('en-US', { timeZone: TIMEZONE, hour: 'numeric', hourCycle: 'h23' }).format(new Date(createdAt)),
  10
);

const getDiaSemanaLocal = (createdAt) => {
  const d = new Date(createdAt).toLocaleDateString('es', { timeZone: TIMEZONE, weekday: 'long' });
  return d.charAt(0).toUpperCase() + d.slice(1);
};

// YYYY-MM-DD en huso Sao Paulo -- para agrupar eventos por día calendario
// real (no por el corte UTC, que en la madrugada podría meter un evento en
// el día equivocado).
const getFechaLocalStr = (createdAt) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(createdAt));

const getFechaLocalCorta = (fechaLocalStr) =>
  new Date(fechaLocalStr + 'T00:00:00').toLocaleDateString('es', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });

const FRANJAS = [
  { label: 'Madrugada (0h-6h)', match: (h) => h >= 0 && h < 6 },
  { label: 'Mañana (6h-12h)', match: (h) => h >= 6 && h < 12 },
  { label: 'Tarde (12h-18h)', match: (h) => h >= 12 && h < 18 },
  { label: 'Noche (18h-24h)', match: (h) => h >= 18 },
];

const uniqSesiones = (lista) => new Set(lista.map((e) => e.session_id).filter(Boolean)).size;

function Ranking({ titulo, items, vacio }) {
  const max = items[0]?.count || 1;
  return (
    <div style={{ marginBottom: 24 }}>
      <p className="section-label">{titulo}</p>
      {items.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--muted)' }}>{vacio}</p>
      ) : (
        items.map((it) => (
          <div key={it.label} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: 'var(--off-white)' }}>{it.label}</span>
              <span style={{ color: 'var(--muted)', fontWeight: 700 }}>{it.count}</span>
            </div>
            <div style={{ background: 'var(--navy-card)', borderRadius: 6, height: 6, overflow: 'hidden' }}>
              <div style={{ width: `${(it.count / max) * 100}%`, height: '100%', background: 'var(--orange)' }} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Embudo({ etapas }) {
  const max = etapas[0]?.count || 1;
  return (
    <div style={{ marginBottom: 24 }}>
      <p className="section-label">RECORRIDO (SESIONES ÚNICAS)</p>
      <div style={{ display: 'flex', gap: 8 }}>
        {etapas.map((et, i) => (
          <div key={et.label} style={{
            flex: 1, background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
            borderRadius: 12, padding: '12px 8px', textAlign: 'center', position: 'relative',
          }}>
            <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--white)' }}>{et.count}</p>
            <p style={{ fontSize: 9.5, color: 'var(--muted)', marginTop: 2, lineHeight: 1.3 }}>{et.label}</p>
            {i > 0 && max > 0 && (
              <p style={{ fontSize: 9, color: 'var(--orange)', marginTop: 4, fontWeight: 700 }}>
                {Math.round((et.count / (etapas[0].count || 1)) * 100)}%
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ExclusionPropia() {
  const [activo, setActivo] = useState(isVuelosTrackingOptedOut());
  const toggle = () => {
    const nuevo = !activo;
    setVuelosTrackingOptOut(nuevo);
    setActivo(nuevo);
  };
  return (
    <div style={{
      background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12,
      padding: 14, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    }}>
      <div>
        <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>No contar mis visitas en este navegador</p>
        <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
          Solo aplica a este dispositivo/navegador. Para el enlace público, ábrelo una vez con{' '}
          <code>?notrack=1</code> al final (ej. .../vuelos-guyana?notrack=1) en cada teléfono/navegador que uses para probarlo -- después de eso ya no hace falta repetirlo ahí. Cada gestor tiene este mismo botón en su propio panel, para que no se cuenten a sí mismos probando su link.
        </p>
      </div>
      <button onClick={toggle} style={{
        flexShrink: 0, fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '6px 12px', whiteSpace: 'nowrap',
        background: activo ? 'rgba(0,196,140,0.15)' : 'var(--navy)',
        color: activo ? 'var(--success)' : 'var(--muted)',
        border: `1px solid ${activo ? 'rgba(0,196,140,0.4)' : 'var(--navy-border)'}`,
      }}>{activo ? '✅ Activado' : 'Desactivado'}</button>
    </div>
  );
}

// ── Dashboard: día por día, con comparación vs. el mismo día de la semana
// anterior y nuevos vs. que regresan. Capado a los últimos 30 días aunque el
// período elegido sea 90 -- día por día más allá de eso deja de ser legible
// en una lista de celular. Necesita eventos de "dias + 7" para poder
// comparar el primer día visible contra su semana anterior.
function DashboardDiario({ eventosExtendidos, dias }) {
  // "Todo" (dias null) no tiene un tope de período -- la vista día por día
  // igual se limita a 30 filas para que siga siendo legible en una lista.
  const diasAMostrar = dias == null ? 30 : Math.min(dias, 30);
  const visitas = eventosExtendidos.filter((e) => e.evento === 'visita');

  // Primera visita histórica de cada sesión, dentro de la ventana
  // descargada -- si esa sesión ya existía antes de esa ventana, esta
  // aproximación la puede marcar como "nueva" sin serlo en realidad.
  const primeraVisitaPorSesion = {};
  visitas.forEach((e) => {
    if (!e.session_id) return;
    const f = getFechaLocalStr(e.created_at);
    if (!primeraVisitaPorSesion[e.session_id] || f < primeraVisitaPorSesion[e.session_id]) {
      primeraVisitaPorSesion[e.session_id] = f;
    }
  });

  const visitasPorDia = {};
  visitas.forEach((e) => {
    const f = getFechaLocalStr(e.created_at);
    if (!visitasPorDia[f]) visitasPorDia[f] = [];
    visitasPorDia[f].push(e);
  });

  const hoy = new Date();
  const filas = [];
  for (let i = 0; i < diasAMostrar; i++) {
    const d = new Date(hoy);
    d.setDate(d.getDate() - i);
    const fechaStr = new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    const deSemanaAnterior = new Date(d);
    deSemanaAnterior.setDate(deSemanaAnterior.getDate() - 7);
    const fechaSemanaAnteriorStr = new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(deSemanaAnterior);

    const eventosDia = visitasPorDia[fechaStr] || [];
    const sesionesDia = new Set(eventosDia.map((e) => e.session_id).filter(Boolean));
    const nuevos = [...sesionesDia].filter((s) => primeraVisitaPorSesion[s] === fechaStr).length;
    const regresan = sesionesDia.size - nuevos;

    const visitasSemanaAnterior = (visitasPorDia[fechaSemanaAnteriorStr] || []).length;
    const hayDatoSemanaAnterior = fechaSemanaAnteriorStr in visitasPorDia || eventosExtendidos.some((e) => getFechaLocalStr(e.created_at) === fechaSemanaAnteriorStr);

    filas.push({
      fechaStr, label: getFechaLocalCorta(fechaStr),
      visitas: eventosDia.length, nuevos, regresan,
      visitasSemanaAnterior, hayDatoSemanaAnterior,
    });
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <p className="section-label">DÍA POR DÍA</p>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
        Muestra los últimos {diasAMostrar} días{(dias == null || dias > 30) ? ` (de ${dias == null ? 'todo el historial' : `los ${dias} seleccionados`} -- más allá de 30 días deja de ser legible en una lista)` : ''}. "Nuevos" son sesiones que no habían visitado antes dentro del rango de datos descargado -- si alguien no visitaba hace mucho tiempo, puede aparecer como "nuevo" sin serlo del todo.
      </p>
      {filas.every((f) => f.visitas === 0) ? (
        <p style={{ fontSize: 12, color: 'var(--muted)' }}>Todavía no hay datos suficientes.</p>
      ) : (
        filas.map((f) => {
          const delta = f.hayDatoSemanaAnterior ? f.visitas - f.visitasSemanaAnterior : null;
          return (
            <div key={f.fechaStr} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
              borderRadius: 10, padding: '10px 12px', marginBottom: 6,
            }}>
              <div>
                <p style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--white)', textTransform: 'capitalize' }}>{f.label}</p>
                <p style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>
                  {f.visitas > 0 ? `Nuevos ${f.nuevos} · Regresan ${f.regresan}` : 'Sin visitas'}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--white)' }}>{f.visitas}</p>
                {delta != null ? (
                  <p style={{ fontSize: 10, fontWeight: 700, color: delta > 0 ? 'var(--success)' : delta < 0 ? 'var(--error)' : 'var(--muted)' }}>
                    {delta > 0 ? '▲' : delta < 0 ? '▼' : '='} {delta > 0 ? '+' : ''}{delta} vs. semana pasada ({f.visitasSemanaAnterior})
                  </p>
                ) : (
                  <p style={{ fontSize: 10, color: 'var(--muted)' }}>Sin dato de la semana pasada</p>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

export default function VuelosEstadisticas() {
  const [vista, setVista] = useState('resumen');
  const [dias, setDias] = useState(30);
  const [canalFiltro, setCanalFiltro] = useState('todos');
  const [eventosExtendidosTodos, setEventosExtendidosTodos] = useState([]);
  const [gestores, setGestores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Se pide una semana extra para poder comparar el primer día visible del
    // Dashboard contra su mismo día de la semana anterior, sin una segunda
    // consulta aparte. Con "Todo" (dias null) no hay corte que extender.
    Promise.all([getVuelosEventos(dias == null ? null : dias + 7), getGestores()]).then(([data, gs]) => {
      setEventosExtendidosTodos(data);
      setGestores(gs);
      setLoading(false);
    });
  }, [dias]);

  if (loading) return (
    <>
      <ExclusionPropia />
      <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Cargando estadísticas...</p>
    </>
  );

  // Filtros: Todos / Propio (sin canal ni gestor) / cada canal fijo (Luis
  // Omar) / cada gestor activo -- estos últimos se arman solos a partir de
  // vuelos_gestores, así un gestor nuevo aparece acá sin tocar código.
  // Prefijo "g:" en el id para no chocar con los códigos de canal.
  const FILTROS_CANAL = [
    { id: 'todos', label: 'Todos' },
    { id: 'propio', label: 'Propio' },
    ...Object.entries(CANALES).map(([id, label]) => ({ id, label })),
    ...gestores.filter((g) => g.activo).map((g) => ({ id: `g:${g.codigo}`, label: g.nombre })),
  ];

  // Cuentas de gestor marcadas "es_prueba" (ej. "Yoedis prueba") no son
  // clientes reales -- se excluyen de cualquier vista agregada (Todos,
  // Propio, otro gestor) para no falsear la estadística, pero siguen
  // visibles si el admin filtra específicamente por ese gestor (para poder
  // confirmar que su propio flujo de prueba funciona).
  const codigosPrueba = new Set(gestores.filter((g) => g.es_prueba).map((g) => g.codigo));
  const excluirPrueba = (lista) => codigosPrueba.size === 0
    ? lista
    : lista.filter((e) => !e.gestor_codigo || !codigosPrueba.has(e.gestor_codigo) || canalFiltro === `g:${e.gestor_codigo}`);

  // 'propio' = sin canal ni gestor marcado (tráfico del grupo de Yoedis). El
  // resto de los cálculos de abajo no cambia -- solo se les pasa la lista ya
  // filtrada, así todo el dashboard (rankings, embudo, etc.) queda separado.
  const filtrarPorCanal = (lista) => canalFiltro === 'todos'
    ? lista
    : canalFiltro === 'propio'
      ? lista.filter((e) => !e.canal && !e.gestor_codigo)
      : canalFiltro.startsWith('g:')
        ? lista.filter((e) => e.gestor_codigo === canalFiltro.slice(2))
        : lista.filter((e) => e.canal === canalFiltro);

  const eventosExtendidos = filtrarPorCanal(excluirPrueba(eventosExtendidosTodos));

  // "Resumen" solo mira el período elegido (7/30/90); el Dashboard usa la
  // ventana extendida completa (+7 días) para las comparaciones semanales.
  // Con "Todo" (dias null) no se recorta nada.
  let eventos = eventosExtendidos;
  if (dias != null) {
    const desdeCorte = new Date();
    desdeCorte.setDate(desdeCorte.getDate() - dias);
    eventos = eventosExtendidos.filter((e) => new Date(e.created_at) >= desdeCorte);
  }

  const visitas = eventos.filter((e) => e.evento === 'visita');
  const visitasPublico = visitas.filter((e) => e.origen === 'publico').length;
  const visitasAutenticado = visitas.filter((e) => e.origen === 'autenticado').length;
  const visitantesUnicos = new Set(visitas.map((e) => e.session_id).filter(Boolean)).size;

  const contarPor = (lista, keyFn) => {
    const mapa = {};
    lista.forEach((e) => {
      const key = keyFn(e);
      if (!key) return;
      mapa[key] = (mapa[key] || 0) + 1;
    });
    return Object.entries(mapa).map(([label, count]) => ({ label, count }));
  };

  const rankingDestinos = topN(contarPor(
    eventos.filter((e) => e.evento === 'ver_destino'),
    (e) => labelDestino(e.destino_categoria)
  ));

  const rankingCiudades = topN(contarPor(
    eventos.filter((e) => e.evento === 'ver_ciudad'),
    (e) => `${e.ciudad_salida} (${labelDestino(e.destino_categoria)})`
  ));

  const rankingFechas = topN(contarPor(
    eventos.filter((e) => e.evento === 'ver_fecha'),
    (e) => `${e.ciudad_salida} → ${e.destino} · ${formatFechaCorta(e.fecha)}`
  ));

  // Ruta cruzada con aerolínea -- ej. "Habana → Guyana · Copa Airlines" --
  // se arma a partir de ver_fecha, el único evento que ya trae la aerolínea
  // de la ruta puntual que se estaba mirando.
  const rankingAerolineas = topN(contarPor(
    eventos.filter((e) => e.evento === 'ver_fecha' && e.aerolinea),
    (e) => `${e.ciudad_salida} → ${e.destino} · ${e.aerolinea}`
  ));

  // ── Recurrencia: cuántas sesiones (dispositivos) volvieron más de una vez ──
  const visitasPorSesion = {};
  visitas.forEach((e) => { if (e.session_id) visitasPorSesion[e.session_id] = (visitasPorSesion[e.session_id] || 0) + 1; });
  const sesionesRecurrentes = Object.values(visitasPorSesion).filter((c) => c > 1).length;

  // ── Duración aproximada de cada visita ──
  // El session_id es del dispositivo y dura para siempre, así que medir "primer evento a último" daba
  // horas o días (alguien que volvía una semana después contaba todo ese tiempo). Ahora los eventos de
  // cada dispositivo se cortan en visitas: una visita nueva empieza cuando pasan más de 30 minutos sin
  // actividad. La duración de cada visita es del primer al último evento de esa visita; las visitas de
  // un solo evento no tienen duración que medir y no cuentan.
  const PAUSA_MAX_MS = 30 * 60 * 1000;
  const tiemposPorSesion = {};
  eventos.forEach((e) => {
    if (!e.session_id) return;
    (tiemposPorSesion[e.session_id] = tiemposPorSesion[e.session_id] || []).push(new Date(e.created_at).getTime());
  });
  const duraciones = [];
  Object.values(tiemposPorSesion).forEach((tiempos) => {
    tiempos.sort((a, b) => a - b);
    let inicio = tiempos[0];
    let anterior = tiempos[0];
    for (let i = 1; i <= tiempos.length; i++) {
      if (i === tiempos.length || tiempos[i] - anterior > PAUSA_MAX_MS) {
        const min = (anterior - inicio) / 60000;
        if (min > 0.05) duraciones.push(min);
        inicio = tiempos[i];
      }
      anterior = tiempos[i];
    }
  });
  const duracionMediaMin = duraciones.length
    ? Math.round((duraciones.reduce((a, b) => a + b, 0) / duraciones.length) * 10) / 10
    : 0;

  // ── Comparadores: sesiones que miraron más de una ruta/ciudad distinta ──
  const vistasPorSesion = {};
  eventos.filter((e) => ['ver_ciudad', 'ver_destino'].includes(e.evento) && e.session_id).forEach((e) => {
    const key = e.evento === 'ver_ciudad' ? `${e.ciudad_salida}|${e.destino_categoria}` : `destino|${e.destino_categoria}`;
    if (!vistasPorSesion[e.session_id]) vistasPorSesion[e.session_id] = new Set();
    vistasPorSesion[e.session_id].add(key);
  });
  const sesionesComparando = Object.values(vistasPorSesion).filter((s) => s.size > 1).length;

  const rankingHorario = FRANJAS
    .map((f) => ({ label: f.label, count: visitas.filter((e) => f.match(getHoraLocal(e.created_at))).length }))
    .sort((a, b) => b.count - a.count);

  const rankingDiaSemana = topN(contarPor(visitas, (e) => getDiaSemanaLocal(e.created_at)), 7);

  const rankingDispositivo = topN(contarPor(
    visitas,
    (e) => (e.device_type === 'movil' ? '📱 Móvil' : e.device_type === 'desktop' ? '💻 Desktop' : null)
  ));

  const rankingNavegador = topN(contarPor(visitas, (e) => e.browser || null));

  // El origen se detecta solo (Facebook, Instagram, TikTok, Google...) -- ver detectarOrigenAutomatico.
  // Sin origen = entró directo o desde WhatsApp (WhatsApp no avisa de dónde viene el clic).
  // Se agrupa sin distinguir mayúsculas para que ?src=Instagram e Instagram detectado sumen juntos.
  const nombreOrigen = (src) => (src ? src.charAt(0).toUpperCase() + src.slice(1).toLowerCase() : 'Directo o WhatsApp');
  const rankingCanal = topN(contarPor(visitas, (e) => nombreOrigen(e.src)));

  const rankingDemandaNoCubierta = topN(contarPor(
    eventos.filter((e) => e.evento === 'sin_resultados'),
    (e) => (e.ciudad_salida ? `${e.ciudad_salida} (${labelDestino(e.destino_categoria)})` : labelDestino(e.destino_categoria))
  ));

  const clicsWhatsapp = eventos.filter((e) => e.evento === 'clic_whatsapp');
  const embudo = [
    { label: 'Entran', count: uniqSesiones(visitas) },
    { label: 'Miran una ruta/ciudad', count: uniqSesiones(eventos.filter((e) => ['ver_ciudad', 'ver_destino'].includes(e.evento))) },
    { label: 'Escriben por WhatsApp', count: uniqSesiones(clicsWhatsapp) },
  ];

  return (
    <div>
      <ExclusionPropia />

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[{ id: 'resumen', label: '📊 Resumen' }, { id: 'dashboard', label: '📅 Dashboard' }].map((v) => (
          <button key={v.id} onClick={() => setVista(v.id)} style={{
            flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
            background: vista === v.id ? 'var(--orange)' : 'var(--navy-card)',
            color: vista === v.id ? 'white' : 'var(--muted)',
            border: '1px solid var(--navy-border)',
          }}>{v.label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {PERIODOS.map((p) => (
          <button key={p.dias} onClick={() => setDias(p.dias)} style={{
            flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: dias === p.dias ? 'var(--orange)' : 'var(--navy-card)',
            color: dias === p.dias ? 'white' : 'var(--muted)',
            border: '1px solid var(--navy-border)',
          }}>{p.label}</button>
        ))}
      </div>

      {FILTROS_CANAL.length > 2 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
          {FILTROS_CANAL.map((f) => (
            <button key={f.id} onClick={() => setCanalFiltro(f.id)} style={{
              flex: '1 0 auto', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              background: canalFiltro === f.id ? 'rgba(255,107,0,0.20)' : 'var(--navy-card)',
              color: canalFiltro === f.id ? 'var(--orange)' : 'var(--muted)',
              border: `1px solid ${canalFiltro === f.id ? 'var(--orange)' : 'var(--navy-border)'}`,
            }}>{f.label}</button>
          ))}
        </div>
      )}

      {vista === 'dashboard' ? (
        <DashboardDiario eventosExtendidos={eventosExtendidos} dias={dias} />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 24 }}>
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{visitas.length}</p>
              <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>VISITAS</p>
            </div>
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{visitantesUnicos}</p>
              <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>ÚNICOS APROX.</p>
            </div>
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{visitasPublico}</p>
              <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>SIN LOGIN</p>
            </div>
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
            {visitasPublico} desde el enlace público y {visitasAutenticado} desde la app con login. "Únicos aprox." se calcula por dispositivo/navegador, no por persona.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 24 }}>
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{sesionesRecurrentes}</p>
              <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>REGRESAN</p>
            </div>
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{duracionMediaMin}<span style={{ fontSize: 12 }}> min</span></p>
              <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>DURACIÓN MEDIA</p>
            </div>
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{clicsWhatsapp.length}</p>
              <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>CLICS WHATSAPP</p>
            </div>
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
            "Regresan": dispositivos que visitaron más de una vez en el período (la señal de interés más fuerte sin login). "Duración media": aproximación (rango entre el primer y el último toque de cada sesión), no un cronómetro real.
            {sesionesComparando > 0 && ` ${sesionesComparando} sesiones miraron más de una ruta o ciudad en la misma visita (comparando opciones).`}
          </p>

          <Embudo etapas={embudo} />

          <Ranking titulo="DESTINOS MÁS REVISADOS" items={rankingDestinos} vacio="Todavía no hay datos suficientes." />
          <Ranking titulo="CIUDADES DE SALIDA MÁS REVISADAS" items={rankingCiudades} vacio="Todavía no hay datos suficientes." />
          <Ranking titulo="FECHAS MÁS REVISADAS" items={rankingFechas} vacio="Todavía no hay datos suficientes." />
          <Ranking
            titulo="RUTAS POR AEROLÍNEA"
            items={rankingAerolineas}
            vacio="Todavía no hay datos suficientes (se registra desde que alguien toca una fecha)."
          />
          <Ranking
            titulo="DEMANDA NO CUBIERTA"
            items={rankingDemandaNoCubierta}
            vacio="Nadie buscó algo que no tuvieras publicado."
          />
          <Ranking titulo="HORARIO MÁS ACTIVO" items={rankingHorario} vacio="Todavía no hay datos suficientes." />
          <Ranking titulo="DÍA DE LA SEMANA MÁS ACTIVO" items={rankingDiaSemana} vacio="Todavía no hay datos suficientes." />
          <Ranking titulo="DISPOSITIVO" items={rankingDispositivo} vacio="Todavía no hay datos suficientes." />
          <Ranking titulo="NAVEGADOR" items={rankingNavegador} vacio="Todavía no hay datos suficientes." />
          <Ranking
            titulo="CANAL DE ORIGEN"
            items={rankingCanal}
            vacio="Todavía no hay datos suficientes. El origen (Facebook, Instagram, Google...) se detecta solo en las visitas nuevas."
          />
        </>
      )}
    </div>
  );
}
